import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import { useRouter } from "next/router";

// 기본 스타일
const getModalStyle = (width, isMobile) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: width,
  maxHeight: "85vh",
  backgroundColor: "#fff",
  // background: "linear-gradient(170deg, #1e3c72 0%, #00173FFF 100%)",
  borderRadius: "20px",
  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
  p: isMobile ? 2 : 5,
  outline: "none",
  overflowY: "auto",
  border: "1px solid rgba(255, 255, 255, 0.18)",
});

// Typography 공통 스타일 - 단어별 줄바꿈 적용
const typographyCommonStyle = {
  wordBreak: "keep-all", // 단어 단위로 줄바꿈
  overflowWrap: "break-word", // 너무 긴 단어는 줄바꿈 허용
  whiteSpace: "pre-line", // 줄바꿈 태그 인식
};

export default function RenewalPopover() {
  const [open, setOpen] = React.useState(true);
  const [modalWidth, setModalWidth] = React.useState("40%");
  const router = useRouter();
  const [isMobile, setIsMobile] = React.useState(false);

  // 현재 경로에 대한 ref 저장 (페이지 이동 감지 용도)
  const prevPathRef = React.useRef(router.asPath);

  // 화면 크기 감지 및 모달 너비 설정
  React.useEffect(() => {
    // 초기 화면 크기에 따른 모달 너비 및 모바일 여부 설정
    handleResize();

    // 화면 크기 변경 시 모달 너비 및 모바일 여부 업데이트
    window.addEventListener("resize", handleResize);

    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // 화면 크기에 따른 모달 너비 및 모바일 여부 계산
  const handleResize = () => {
    const width = window.innerWidth;
    setIsMobile(width <= 450);
    if (width <= 600) {
      setModalWidth("90%"); // 모바일 사이즈
    } else if (width <= 1024) {
      setModalWidth("80%"); // 태블릿 사이즈
    } else {
      setModalWidth("60%"); // 데스크탑 사이즈
    }
  };

  React.useEffect(() => {
    // 페이지가 새로 로드될 때마다 모달을 표시
    setOpen(true);

    // 페이지 이동 감지
    const handleRouteChange = (url) => {
      // url이 변경됨 = 페이지 이동이 발생
      prevPathRef.current = url;
    };

    // 이벤트 리스너 등록
    router.events.on("routeChangeComplete", handleRouteChange);

    // 컴포넌트 언마운트 시 정리
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, []);

  // 라우터 변경 감지 (페이지 이동 감지)
  React.useEffect(() => {
    // 경로가 변경되면 모달 상태 초기화
    if (router.asPath !== prevPathRef.current) {
      prevPathRef.current = router.asPath;
      setOpen(true);
    }
  }, [router.asPath]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleButtonClick = () => {
    // TODO: 홈페이지 완성시 주소 수정해서 빌드.
    window.open("https://enkino.com", "_blank");
    setOpen(false);
  };

  return (
    <div>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            backdropFilter: "blur(10px)",
          },
        }}
      >
        <Box sx={getModalStyle(modalWidth, isMobile)}>
          <Typography
            id="modal-modal-title"
            variant="h4"
            component="h2"
            sx={{
              ...typographyCommonStyle,
              fontWeight: "900",
              textAlign: "center",
              mt: 2,
              pb: 5,
              fontSize: isMobile ? "28px" : "42px",
              color: "#1e3c72",
              letterSpacing: "2px",
              borderBottom: "1px solid rgba(0, 0, 0, 0.2)",
            }}
          >
            홈페이지 리모델링 안내
          </Typography>

          <Box
            sx={{
              backdropFilter: "blur(10px)",
              borderRadius: "15px",
              p: isMobile ? 2 : 3,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                ...typographyCommonStyle,
                textAlign: "center",
                fontSize: isMobile ? "16px" : "24px",
                lineHeight: 2,
                color: "#333",
                fontWeight: "900",
                px: 2,
                py: 2,
              }}
            >
              안녕하세요, enkino AI 입니다.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                ...typographyCommonStyle,
                textAlign: "center",
                fontSize: isMobile ? "14px" : "20px",
                lineHeight: 2,
                color: "#333",
                fontWeight: "500",
                px: 2,
                py: 2,
              }}
            >
              더 나은 서비스 제공을 위해 홈페이지를 전면 리모델링 중입니다.
              <br />
              새로운 enkino AI는 더욱 강력한 인공지능 서비스를 제공할 예정입니다.
              <br />
              리모델링 기간 동안 일부 서비스 이용에 불편이 있을 수 있으니 양해 부탁드립니다.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 3,
              pt: 5,
              borderTop: "1px solid rgba(0, 0, 0, 0.2)",
            }}
          >
            <Button
              onClick={handleButtonClick}
              variant="contained"
              sx={{
                minWidth: isMobile ? "100%" : "300px",
                borderRadius: "3px",
                wordBreak: "keep-all", // 단어 단위로 줄바꿈
                overflowWrap: "break-word", // 너무 긴 단어는 줄바꿈 허용
                whiteSpace: "pre-line", // 줄바꿈 태그 인식
                fontSize: isMobile ? "16px" : "18px",
                fontWeight: "bold",
                py: 2,
                px: 4,
                background: "#204487FF",
                color: "#fff",
                transition: "all 0.2s ease",
                "&:hover": {
                  background: "#0C2043FF",
                  transform: "scale(1.02)",
                  boxShadow: "0 6px 20px rgba(0, 0, 0, 0.3)",
                },
              }}
            >
              ENKINO AI 바로가기
            </Button>
          </Box>

          <Box
            sx={{
              pt: 5,
            }}
          >
            <Typography
              sx={{
                ...typographyCommonStyle,
                textAlign: "center",
                fontSize: isMobile ? "14px" : "16px",
                color: "#333",
                mb: 1,
              }}
            >
              enkino AI 주식회사
            </Typography>
            <Typography
              sx={{
                ...typographyCommonStyle,
                textAlign: "center",
                fontSize: isMobile ? "12px" : "14px",
                color: "#333",
              }}
            >
              대표이사: 성기범 | 경기도 안양시 동안구 시민대로 187
            </Typography>
          </Box>
        </Box>
      </Modal>
    </div>
  );
}
